/**
 * Integrations screen — admin view of publisher third-party connections
 * (Shopify, WooCommerce, Discord, Telegram, coupon issuance).
 *
 * Scoped port:
 *   - Overview tab — KPI cards across all four integration types +
 *     coupon totals + redemption rate.
 *   - eCommerce tab — Shopify + WooCommerce list with provider/status
 *     filters, click-to-detail modal, activate / pause / disconnect
 *     status mutation, recent-coupons table per integration.
 *
 * Deferred (in-screen legacy deep-link cards on the Overview tab):
 *   - Discord guild list + detail
 *   - Telegram users + quests
 *   - Admin coupon grid
 *
 * Rationale for the deferral: each of those three sub-tabs has its own
 * provider-specific UI (bot config, quest editor, etc.) that doesn't
 * yet pay off until daily-use workflow proves it. The deep links keep
 * staff unblocked in the meantime.
 */
import { useMemo, useState } from 'react';
import { ExternalLink, X } from 'lucide-react';
import {
  EcomIntegrationRow,
  EcomProvider,
  IntegrationStatus
} from '@/api/integrations';
import {
  useEcommerceIntegration,
  useEcommerceIntegrations,
  useIntegrationsOverview,
  useUpdateEcommerceIntegration
} from '@/hooks/useIntegrations';
import {
  ActionButton,
  Card,
  Pagination,
  SectionLabel,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtDate, fmtDateTime, fmtNum } from '@/lib/format';

const TYPE_BADGE: Record<string, string> = {
  shopify: 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30',
  woocommerce: 'bg-purple-500/15 text-purple-300 ring-1 ring-purple-500/30',
  discord: 'bg-indigo-500/15 text-indigo-300 ring-1 ring-indigo-500/30',
  telegram: 'bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/30'
};

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  paused: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  error: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  disconnected: 'bg-white/10 text-nurev-muted ring-1 ring-white/10'
};

function TypeBadge({ type }: { type: string }) {
  const cls =
    TYPE_BADGE[type] || 'bg-white/10 text-nurev-muted ring-1 ring-white/10';
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-xs font-medium capitalize ${cls}`}
    >
      {type}
    </span>
  );
}

function StatusBadge({ status }: { status?: string }) {
  const cls =
    STATUS_BADGE[status || ''] ||
    'bg-white/10 text-nurev-muted ring-1 ring-white/10';
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${cls}`}
    >
      {status || '—'}
    </span>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────

function OverviewTab() {
  const { data, isLoading } = useIntegrationsOverview();
  const ecom = data?.ecommerce || {};
  const discord = data?.discord || { available: false };
  const telegram = data?.telegram || { available: false };

  const totalCoupons = ecom.totalCoupons || 0;
  const redeemed = ecom.redeemedCoupons || 0;
  const redemptionRate =
    totalCoupons > 0 ? `${Math.round((redeemed / totalCoupons) * 100)}%` : '—';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <StatCard
          label="Shopify shops"
          value={fmtNum(ecom.shopify?.active)}
          sub={`${ecom.shopify?.total || 0} total`}
          loading={isLoading}
        />
        <StatCard
          label="WooCommerce sites"
          value={fmtNum(ecom.woocommerce?.active)}
          sub={`${ecom.woocommerce?.total || 0} total`}
          loading={isLoading}
        />
        <StatCard
          label="Discord guilds"
          value={discord.available ? fmtNum(discord.guilds) : '—'}
          sub={
            discord.available
              ? `${fmtNum(discord.users)} users`
              : 'DISCORD_BOT_MONGO_URI not set'
          }
          color={discord.error ? 'text-red-300' : undefined}
          loading={isLoading}
        />
        <StatCard
          label="Telegram users"
          value={telegram.available ? fmtNum(telegram.users) : '—'}
          sub={
            telegram.available
              ? `${telegram.quests || 0} active quests`
              : 'TELEGRAM_BOT_MONGO_URI not set'
          }
          color={telegram.error ? 'text-red-300' : undefined}
          loading={isLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <StatCard
          label="Coupons issued"
          value={fmtNum(totalCoupons)}
          sub={`${fmtNum(redeemed)} redeemed`}
        />
        <StatCard label="Redemption rate" value={redemptionRate} />
      </div>

      <Card className="border-amber-500/20 bg-amber-500/[0.03]">
        <SectionLabel>Tabs still on legacy</SectionLabel>
        <p className="mb-3 text-xs text-nurev-muted">
          Three integration sub-tabs ship on this screen later. They have
          provider-specific UIs (bot configs, quest editor, coupon policy
          grid) that don't pay off until daily-use workflow asks for them.
          Until then, hit them on app.nurev.io:
        </p>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <DeepLinkCard
            label="Discord guilds"
            sub="Bot setup, per-guild stats, role mappings"
            href="https://app.nurev.io/integrations?tab=discord"
          />
          <DeepLinkCard
            label="Telegram bot"
            sub="Bot users, quest editor, broadcast tools"
            href="https://app.nurev.io/integrations?tab=telegram"
          />
          <DeepLinkCard
            label="Admin coupons"
            sub="All issued coupons across all integrations"
            href="https://app.nurev.io/integrations?tab=coupons"
          />
        </div>
      </Card>
    </div>
  );
}

function DeepLinkCard({
  label,
  sub,
  href
}: {
  label: string;
  sub: string;
  href: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="group flex items-start gap-3 rounded-lg bg-nurev-surface p-3 ring-1 ring-amber-500/20 transition hover:bg-amber-500/[0.05] hover:ring-amber-500/40"
    >
      <div className="flex-1">
        <p className="text-sm font-medium text-amber-200 group-hover:text-amber-100">
          {label}
        </p>
        <p className="mt-0.5 text-[10px] text-nurev-muted">{sub}</p>
      </div>
      <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" />
    </a>
  );
}

// ─── eCommerce tab ────────────────────────────────────────────────────────

function EcommerceTab({
  onSelect
}: {
  onSelect: (id: string) => void;
}) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [type, setType] = useState<EcomProvider | ''>('');
  const [status, setStatus] = useState<IntegrationStatus | ''>('');

  const params = useMemo(
    () => ({
      page,
      limit: 25,
      q: q.trim() || undefined,
      type: type || undefined,
      status: status || undefined
    }),
    [page, q, type, status]
  );
  const { data, isLoading } = useEcommerceIntegrations(params);
  const docs = data?.docs || [];
  const totalPages = data?.totalPages || 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <input
          value={q}
          onChange={e => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="Search shop, remoteId…"
          className="w-64 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text placeholder:text-nurev-muted focus:border-amber-500/50 focus:outline-none"
        />
        <select
          value={type}
          onChange={e => {
            setType(e.target.value as EcomProvider | '');
            setPage(1);
          }}
          className="rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
        >
          <option value="">All providers</option>
          <option value="shopify">Shopify</option>
          <option value="woocommerce">WooCommerce</option>
        </select>
        <select
          value={status}
          onChange={e => {
            setStatus(e.target.value as IntegrationStatus | '');
            setPage(1);
          }}
          className="rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
        >
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="error">Error</option>
          <option value="disconnected">Disconnected</option>
        </select>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Name / Remote</th>
                <th className="px-4 py-3 font-medium">Publisher</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Coupons</th>
                <th className="px-4 py-3 font-medium">Connected</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-nurev-border/50">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : docs.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-nurev-muted"
                  >
                    No eCommerce integrations match these filters.
                  </td>
                </tr>
              ) : (
                docs.map(i => (
                  <EcomRow key={i._id} i={i} onSelect={() => onSelect(i._id)} />
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="border-t border-nurev-border px-4 py-3">
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}

function EcomRow({
  i,
  onSelect
}: {
  i: EcomIntegrationRow;
  onSelect: () => void;
}) {
  return (
    <tr
      onClick={onSelect}
      className="cursor-pointer border-b border-nurev-border/50 transition-colors hover:bg-white/[0.02]"
    >
      <td className="px-4 py-2">
        <TypeBadge type={i.type} />
      </td>
      <td className="px-4 py-2 text-nurev-text">
        <div>{i.name || '—'}</div>
        {i.remoteId && (
          <div className="text-xs text-nurev-muted">{i.remoteId}</div>
        )}
      </td>
      <td className="px-4 py-2 text-xs text-nurev-text">
        {i.publisherId?.email ||
          i.publisherId?.publisherKey ||
          i.publisherId?.name ||
          '—'}
      </td>
      <td className="px-4 py-2">
        <StatusBadge status={i.status} />
      </td>
      <td className="px-4 py-2 text-right font-mono text-nurev-text">
        {fmtNum(i.stats?.totalRedemptions)}
      </td>
      <td className="px-4 py-2 text-xs text-nurev-muted">
        {i.connectedAt ? fmtDate(i.connectedAt) : '—'}
      </td>
    </tr>
  );
}

// ─── Detail modal ─────────────────────────────────────────────────────────

function EcomDetailModal({
  id,
  onClose
}: {
  id: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useEcommerceIntegration(id);
  const update = useUpdateEcommerceIntegration();
  const [actionError, setActionError] = useState<string | null>(null);

  const integration = data?.integration;
  const coupons = data?.recentCoupons || [];

  const setStatus = async (next: IntegrationStatus) => {
    setActionError(null);
    if (next === 'disconnected') {
      if (
        !window.confirm(
          'Disconnect this integration? The publisher will need to reconnect.'
        )
      ) {
        return;
      }
    }
    try {
      await update.mutateAsync({ id, status: next });
    } catch (err) {
      setActionError(
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Status change failed.'
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-nurev-border bg-nurev-surface-lift p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="mb-2 flex items-center gap-2">
              {integration?.type && <TypeBadge type={integration.type} />}
              <StatusBadge status={integration?.status} />
            </div>
            <h3 className="text-lg font-semibold text-nurev-text">
              {integration?.name || integration?.remoteId || 'Integration'}
            </h3>
            <div className="mt-1 text-xs text-nurev-muted">
              Publisher:{' '}
              {integration?.publisherId?.email ||
                integration?.publisherId?.publisherKey ||
                '—'}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-nurev-muted hover:bg-white/5 hover:text-nurev-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : !integration ? (
          <p className="text-sm text-nurev-muted">Integration not found.</p>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap gap-2">
              {integration.status !== 'active' && (
                <ActionButton
                  variant="positive"
                  size="sm"
                  disabled={update.isPending}
                  onClick={() => setStatus('active')}
                >
                  Activate
                </ActionButton>
              )}
              {integration.status !== 'paused' && (
                <ActionButton
                  variant="info"
                  size="sm"
                  disabled={update.isPending}
                  onClick={() => setStatus('paused')}
                >
                  Pause
                </ActionButton>
              )}
              {integration.status !== 'disconnected' && (
                <ActionButton
                  variant="destructive"
                  size="sm"
                  disabled={update.isPending}
                  onClick={() => setStatus('disconnected')}
                >
                  Disconnect
                </ActionButton>
              )}
            </div>

            {actionError && (
              <p className="mb-3 text-xs text-red-300">{actionError}</p>
            )}

            <div className="mb-4">
              <SectionLabel>Coupon Policy</SectionLabel>
              <pre className="overflow-x-auto rounded-lg border border-nurev-border bg-nurev-surface p-3 text-xs text-nurev-muted">
                {JSON.stringify(
                  (integration.config as { couponPolicy?: unknown })?.couponPolicy ||
                    {},
                  null,
                  2
                )}
              </pre>
            </div>

            <div>
              <SectionLabel>Recent Coupons ({coupons.length})</SectionLabel>
              {coupons.length === 0 ? (
                <p className="text-sm text-nurev-muted">
                  No coupons issued yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                        <th className="pb-2 font-medium">Code</th>
                        <th className="pb-2 font-medium">Discount</th>
                        <th className="pb-2 font-medium">Status</th>
                        <th className="pb-2 font-medium">Issued</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-nurev-border">
                      {coupons.map(c => (
                        <tr key={c._id}>
                          <td className="py-2 font-mono text-xs text-nurev-text">
                            {c.code}
                          </td>
                          <td className="py-2 text-nurev-text">
                            {c.discountType === 'percent'
                              ? `${c.discountValue}%`
                              : `$${((c.discountValue || 0) / 100).toFixed(2)}`}
                          </td>
                          <td className="py-2">
                            <StatusBadge status={c.status} />
                          </td>
                          <td className="py-2 text-xs text-nurev-muted">
                            {fmtDateTime(c.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'ecommerce', label: 'eCommerce' }
] as const;
type Tab = (typeof TABS)[number]['id'];

export function IntegrationsScreen() {
  const [tab, setTab] = useState<Tab>('overview');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Integrations
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            Third-party publisher connections: Shopify, WooCommerce, Discord,
            Telegram, and coupon issuance. eCommerce status can be toggled here;
            Discord / Telegram / Coupons tabs still link to the legacy view.
          </p>
        </div>
        <a
          href="https://app.nurev.io/integrations"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-nurev-muted hover:text-amber-300"
          title="Open the legacy view on the publisher dashboard"
        >
          legacy view
          <ExternalLink className="h-3 w-3" />
        </a>
      </header>

      <div className="flex w-fit gap-1 rounded-lg bg-white/[0.03] p-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-md px-4 py-2 text-xs font-medium transition ${
              tab === t.id
                ? 'bg-amber-500 text-nurev-bg'
                : 'text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'ecommerce' && (
        <EcommerceTab onSelect={id => setSelectedId(id)} />
      )}

      {selectedId && (
        <EcomDetailModal
          id={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
